import { Control, FieldValues, Path } from "react-hook-form";
import { FormControl, FormField, FormItem, FormLabel } from "./Form";
import { formInputClassName } from "../../../styles/input";
import { GoogleMap, OverlayView, OverlayViewF } from "@react-google-maps/api";
import { CSSProperties, useState } from "react";
import Loading from "../Loading";
import { Svg } from "../Svg";
import IconButton from "../IconButton";
import { MapControl } from "../../shared/MapControl";

const baseCenter: google.maps.LatLngLiteral = {
  lat: 46.60079157391875,
  lng: 2.4799331427083393,
};

const mapContainerStyle: CSSProperties = {
  width: "100%",
  height: "450px",
};

const mapStyles: google.maps.MapTypeStyle[] = [
  {
    featureType: "all",
    stylers: [{ lightness: 50 }],
  },
  {
    featureType: "poi",
    elementType: "all",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "transit",
    elementType: "all",
    stylers: [{ visibility: "off" }],
  },
];

const mapOptions: google.maps.MapOptions = {
  styles: mapStyles,
  mapTypeId: "terrain",
  disableDefaultUI: true,
  zoomControl: true,
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: false,
};

// import { AddressForm } from "@ac-expo/api"; ??
export type AddressForm = {
  address: string | null;
  lat: number | null;
  long: number | null;
  zipCode: string | null;
  city: string | null;
  country: string | null;
};

type FormMapProps<TForm extends FieldValues> = {
  label?: string;
  control: Control<TForm>;
  name: Path<TForm>;
  onChange?: (values: AddressForm) => boolean | void;
  showUserGeolocationButton?: boolean;
  isMapApiLoaded: boolean;
  disabled?: boolean;
};

export function FormMap<TForm extends FieldValues>({
  isMapApiLoaded,
  label,
  control,
  name,
  onChange,
  showUserGeolocationButton,
  disabled,
}: FormMapProps<TForm>) {
  if (!isMapApiLoaded) return <Loading />;

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={formInputClassName}>
          {label && <FormLabel>{label}</FormLabel>}

          <FormControl>
            <MapField
              values={field.value}
              onChange={(values) => {
                if (disabled) return;
                if (onChange && onChange(values) === false) return;
                field.onChange(values);
              }}
              showUserGeolocationButton={showUserGeolocationButton}
            />
          </FormControl>
        </FormItem>
      )}
    />
  );
}

function MapField({
  values,
  onChange,
  showUserGeolocationButton,
}: {
  values: AddressForm;
  onChange: (values: AddressForm) => void;
  showUserGeolocationButton?: boolean;
}) {
  const [center, setCenter] = useState<google.maps.LatLngLiteral>({
    lat: values.lat ?? baseCenter.lat,
    lng: values.long ?? baseCenter.lng,
  });
  const [zoom, setZoom] = useState(values.lat && values.long ? 15 : 6);

  const handleMapClick = (event: google.maps.MapMouseEvent) => {
    const lat = event.latLng?.lat() ?? null;
    const lng = event.latLng?.lng() ?? null;

    if (lat && lng) {
      onChange({
        address: null,
        lat: lat,
        long: lng,
        zipCode: null,
        city: null,
        country: null,
      });
    }
  };

  const handleMarkerClick = () => {
    onChange({
      address: null,
      lat: null,
      long: null,
      zipCode: null,
      city: null,
      country: null,
    });
  };

  const getLocation = () => {
    const geolocation = navigator.geolocation;
    if (geolocation) {
      geolocation.getCurrentPosition((position) => {
        onChange({
          address: null,
          lat: position.coords.latitude,
          long: position.coords.longitude,
          zipCode: null,
          city: null,
          country: null,
        });

        setCenter({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });

        setZoom(15);
      });
    }
  };

  return (
    <GoogleMap
      mapContainerStyle={mapContainerStyle}
      center={center}
      zoom={zoom}
      options={mapOptions}
      onClick={handleMapClick}
    >
      {showUserGeolocationButton && (
        <MapControl position="RIGHT_TOP">
          <div className="mr-2 mt-2">
            <IconButton
              svgType="crosshair"
              svgProps={{ size: 24, className: "text-blue-500" }}
              className="px-1"
              type="button"
              variant={"outline-blue"}
              onClick={getLocation}
            />
          </div>
        </MapControl>
      )}
      {values.lat && values.long && (
        <OverlayViewF
          key={`${values.lat}-${values.long}`}
          position={{ lat: values.lat, lng: values.long }}
          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
        >
          <div
            className="custom-marker flex size-10 cursor-pointer items-center justify-center "
            onClick={(e) => {
              e.stopPropagation();
              handleMarkerClick();
            }}
            onKeyDown={(e) => {
              e.stopPropagation();
              handleMarkerClick();
            }}
            role="button"
            tabIndex={0}
            style={{ transform: "translate(-50%, -100%)" }}
          >
            <Svg name="doorPin" alt="Entrée" />
          </div>
        </OverlayViewF>
      )}
    </GoogleMap>
  );
}

export default FormMap;
